// Help text for: Historical > Insights (enSight).
// Keys are referenced as "ensight.<field>" by <FieldHelp id=... />. Entry shape:
//   title  - field name as shown on screen
//   use    - what the field is for / how to read it
//   source - measured | calculated | backend | default | setting | input
//   how    - where the value comes from: formula, default value, or who sets it

// The loss formula is shared by every insight, so it is written once.
const LOSS_FORMULA =
  "Worth of Equipment × Probability of replacement, plus Production Value per hour × hours the problem lasted, plus Maintenance Cost × Probability of maintenance";

const help = {
  // ===== Page header =====
  criticalCount: {
    title: "Critical",
    use: "How many of the problems on screen are Critical, meaning something crossed the critical limit at least once.",
    source: "calculated",
    how: "Counts the rows you can see now, after the branch, machine, severity and category filters. A sub-meter is counted too, even when the meter above it reports the same problem.",
  },
  warningCount: {
    title: "Warning",
    use: "How many of the problems on screen are Warnings: the warning limit was crossed but the critical one never was.",
    source: "calculated",
    how: "Counts the rows you can see now, after the branch, machine, severity and category filters. Sub-meters are counted too.",
  },

  // ===== Filters =====
  branch: {
    title: "Branch",
    use: "Narrows the problems down to the machines under one branch.",
    source: "input",
    how: "Follows the branch picker in the top bar, so change it there. \"All\" covers every machine on your account.",
  },
  fromDate: {
    title: "From",
    use: "First day of the period that gets checked for problems.",
    source: "input",
    how: "Comes pre-filled with 2 days before today, so the screen opens on the last 3 days. Pick a date, then press Apply. The 3D / 7D / 30D buttons set and apply a range ending today in one click.",
  },
  toDate: {
    title: "To",
    use: "Last day of the period that gets checked. The whole day is included.",
    source: "input",
    how: "Comes pre-filled with today. Press Apply after you change it. Apply stays greyed out until the range changes and From is on or before To.",
  },
  machine: {
    title: "Machine",
    use: "Shows only the problems found on one machine.",
    source: "input",
    how: "Lists the machines on your account, narrowed to the branch you picked. It filters what is already on screen, so nothing is checked again. With one machine picked, the totals cover every row you can see.",
  },
  severity: {
    title: "Severity",
    use: "Shows only Critical problems, or only Warnings.",
    source: "input",
    how: "Filters what is already on screen. It does not change how Critical or Warning is decided.",
  },
  category: {
    title: "Category",
    use: "Shows one family of checks, such as Voltage or Power Factor. The summary card titles change to match.",
    source: "input",
    how: "The families are Voltage, Current, Power, Power Factor, Energy, Frequency, THD Voltage and THD Current.",
  },

  // ===== Summary cards =====
  totalLoss: {
    title: "Total Loss",
    use: "Rough money lost to all the problems on screen, over the period you chose.",
    source: "calculated",
    how: "Adds up the Loss (PKR) of the rows you can see. With Machine set to \"All\", a sub-meter is left out when the meter above it reported a problem too, so the same loss is not counted twice.",
  },
  totalRemedyCost: {
    title: "Estimated Total Cost",
    use: "Rough spend needed to put right every problem on screen.",
    source: "calculated",
    how: "Adds up the Est. Fix Cost of the rows you can see, skipping sub-meters already covered by the meter above them. Same rule as Total Loss.",
  },
  totalSavings: {
    title: "Total Savings Potential",
    use: "What you would save if every recommended fix were carried out.",
    source: "calculated",
    how: "Not shown yet (Coming Soon). For now each problem's saving is set equal to its loss.",
  },

  // ===== Table =====
  sortOrder: {
    title: "Sorted: Critical first",
    use: "Explains the order the rows come in.",
    source: "backend",
    how: "Critical comes before Warning, and inside each group the biggest Loss (PKR) sits at the top.",
  },
  insight: {
    title: "Insight",
    use: "The kind of problem that was found. Hover a name to see its exact rule.",
    source: "backend",
    how: "Found by the enSight checks, which run over every minute of readings in the period you chose. One row per kind of problem per machine.",
  },
  machineCol: {
    title: "Machine",
    use: "The machine or meter the problem was found on.",
    source: "backend",
    how: "The name you gave it under Node Settings. Peak-demand (MDI) problems are reported on the grid meter, or on \"Grid (combined)\" when you have several.",
  },
  categoryCol: {
    title: "Category",
    use: "Which family of checks raised this. Hover a badge to see what that family covers.",
    source: "backend",
    how: "Set by the check that found the problem, and it matches the Category filter.",
  },
  severityCol: {
    title: "Severity",
    use: "How serious the problem is.",
    source: "backend",
    how: "Critical if any qualifying breach in the period crossed the critical limit, otherwise Warning. Each problem's limits are listed when you hover its name.",
  },
  detected: {
    title: "Detected",
    use: "When the problem first happened in the period you chose.",
    source: "backend",
    how: "Start and end of the first qualifying breach. Shown as HH:MM for a single day, or as date plus time when the range covers several days.",
  },
  loss: {
    title: "Loss (PKR)",
    use: "Rough money lost because of this problem over the period you chose.",
    source: "calculated",
    how: `${LOSS_FORMULA}. It uses this machine's figures from Info > Node Settings > Loss Calculation Parameters. Left blank, they fall back to PKR 5,000,000, PKR 5,000/hr, PKR 5,000, 0.20 and 0.20.`,
  },
  fixCost: {
    title: "Est. Fix Cost (PKR)",
    use: "Rough cost of the main recommended fix for this problem.",
    source: "backend",
    how: "The standard figure for the first recommended fix, from enSight's price list. The smaller figure underneath is that fix's low-to-high range. Fixes not on the list are priced at PKR 25,000 (range 10,000 to 50,000).",
  },
  totals: {
    title: "Totals",
    use: "The loss and fix-cost columns added up for the rows on screen.",
    source: "calculated",
    how: "The same figures as the Total Loss and Estimated Total Cost cards, so sub-meters covered by the meter above them are not added twice. The count of rows includes every row you can see.",
  },

  // ===== Detail popup =====
  lossEquipment: {
    title: "Equipment",
    use: "What this problem could cost if it damages the machine badly enough to need replacing.",
    source: "calculated",
    how: "Worth of Equipment × Probability of replacement. Left blank: PKR 5,000,000 × 0.20 = PKR 1,000,000. A Worth of Equipment under PKR 500,000 is lifted to 500,000. Set both under Info > Node Settings.",
  },
  lossDowntime: {
    title: "Downtime",
    use: "Production lost while the problem lasted.",
    source: "calculated",
    how: "Production Value per hour (PKR 5,000 when blank, never under 1,000) × hours affected. Voltage, power factor and frequency count each breach as at least 1 hour; power and THD use the real minutes; energy uses the extra hours the surplus kWh works out to.",
  },
  lossMaintenance: {
    title: "Maintenance",
    use: "What the maintenance visit this problem is likely to cause would cost.",
    source: "calculated",
    how: "Maintenance Cost × Probability of maintenance. Left blank: PKR 5,000 × 0.20 = PKR 1,000. A Maintenance Cost under PKR 1,000 is lifted to 1,000. Set both under Info > Node Settings.",
  },
  remedyRange: {
    title: "Range",
    use: "Likely lowest and highest cost of the main recommended fix.",
    source: "backend",
    how: "The low and high figures for the first recommended fix in enSight's price list. Shown only when the two differ.",
  },
  payback: {
    title: "Payback",
    use: "Roughly how many months the fix would take to pay for itself.",
    source: "calculated",
    how: "Est. Fix Cost ÷ savings × 12 months. Savings is set equal to this problem's loss for the period you chose.",
  },
  trendChart: {
    title: "Trend chart",
    use: "Readings around the first breach, drawn against the limit that was crossed.",
    source: "measured",
    how: "Up to 15 readings taken at the breaker, starting at the first breach. The red dashed line is the critical limit if the problem is Critical, otherwise the warning limit.",
  },
  rootCauses: {
    title: "Likely Root Causes",
    use: "Common reasons this kind of problem happens, to point your search in the right direction.",
    source: "backend",
    how: "A fixed list for each kind of problem, written by enSight's engineers. It is not worked out from your readings.",
  },
  fixes: {
    title: "Recommended Fixes",
    use: "Work that usually clears this kind of problem, with rough costs.",
    source: "backend",
    how: "A fixed list for each kind of problem. Each fix is priced from enSight's price list, and the first one sets the Est. Fix Cost.",
  },
  fixType: {
    title: "Fix type",
    use: "What sort of work the fix is: Equipment, Maintenance, Engineering, Controls, Behavior, Facility or Testing.",
    source: "backend",
    how: "Comes from enSight's price list for that fix.",
  },
  fixEstCost: {
    title: "Est. Cost",
    use: "Typical cost of this one fix, with its low-to-high range beside it.",
    source: "backend",
    how: "Standard, low and high figures in PKR from enSight's price list. They are general figures, not a quote for your site.",
  },
  fixBasis: {
    title: "Basis",
    use: "What the cost covers, such as per motor or per panel.",
    source: "backend",
    how: "Comes from enSight's price list.",
  },
  fixNote: {
    title: "Note",
    use: "What to check to turn the rough figure into a real price.",
    source: "backend",
    how: "Comes from enSight's price list.",
  },
  occurrences: {
    title: "Occurrence Windows",
    use: "The separate spells in which the problem happened.",
    source: "backend",
    how: "Start and end of each qualifying breach, up to the first 5. Shown only when there is more than one.",
  },

  // ===== Categories (badge) =====
  catVoltage: {
    title: "Voltage",
    use: "Checks the supply voltage: dips, rises, too low, too high, uneven phases and spikes.",
    source: "backend",
    how: "Uses the average of the three phase-to-neutral voltages against a normal 230 V. Readings averaging 50 V or less are treated as the machine being off and skipped.",
  },
  catCurrent: {
    title: "Current",
    use: "Checks the phase currents: uneven phases, a lost phase, current flowing while off, and start-up surge.",
    source: "backend",
    how: "A machine counts as on when any phase carries more than 0.5 A. The current-while-off and surge checks need the machine's Rated Power (Info > Node Settings).",
  },
  catPower: {
    title: "Power",
    use: "Checks power against the machine's rating, against its working hours, and against the site's peak demand.",
    source: "backend",
    how: "Needs Rated Power and Working Hours for each machine (Info > Node Settings) and your Sanctioned Load (Info > Details). A check whose figure is missing is skipped, never guessed.",
  },
  catPowerFactor: {
    title: "Power Factor",
    use: "Looks for power factor that is low, leading or jumping about.",
    source: "backend",
    how: "Only readings taken while the machine draws more than 0.5 kW are checked. A positive power factor counts as lagging, a negative one as leading.",
  },
  catEnergy: {
    title: "Energy",
    use: "Compares daily and monthly energy with the recent past (consumption and generator) or with the weather forecast (solar).",
    source: "backend",
    how: "Uses hourly energy readings. A day needs at least 12 hours of readings and a month at least 20 days, otherwise it is skipped.",
  },
  catFrequency: {
    title: "Frequency",
    use: "Checks the supply frequency for dips, rises and swinging.",
    source: "backend",
    how: "Compares minute-by-minute readings against a 50 Hz supply's limits of 48.5 Hz and 51.5 Hz. Readings of 10 Hz or less are skipped.",
  },
  catThdVoltage: {
    title: "THD Voltage",
    use: "Checks how much harmonics have distorted the shape of the voltage.",
    source: "backend",
    how: "Uses the voltage distortion (%) read at the breaker, averaged over each minute. Warning above 5%, critical above 8%.",
  },
  catThdCurrent: {
    title: "THD Current",
    use: "Checks how much harmonics have distorted the shape of the current.",
    source: "backend",
    how: "Uses the current distortion (%) read at the breaker, averaged over each minute. Warning above 10%, critical above 20%.",
  },

  // ===== Insight types (name in the table and the popup title) =====
  typeVoltageSag: {
    title: "Voltage Sag",
    use: "A short dip in voltage, usually when a big motor starts. It can reset controllers and slow production.",
    source: "backend",
    how: "Average voltage below 207 V (90% of 230 V) for 3 minutes or less. Critical below 184 V (80%).",
  },
  typeVoltageSwell: {
    title: "Voltage Swell",
    use: "A short rise in voltage, for example when load drops off or capacitors switch in. It can damage electronics.",
    source: "backend",
    how: "Average voltage above 253 V (110% of 230 V) for 3 minutes or less. Critical above 276 V (120%).",
  },
  typeUnderVoltage: {
    title: "Under Voltage",
    use: "Supply voltage sitting low, which pushes current up, heats motors and shortens their life.",
    source: "backend",
    how: "Average voltage below 207 V (90% of 230 V). Critical below 184 V (80%). Every low spell counts, short ones included.",
  },
  typeOverVoltage: {
    title: "Over Voltage",
    use: "Supply voltage sitting high, which stresses insulation and makes a failure more likely.",
    source: "backend",
    how: "Average voltage above 253 V (110% of 230 V). Critical above 276 V (120%). Every high spell counts, short ones included.",
  },
  typeVoltageUnbalance: {
    title: "Voltage Unbalance",
    use: "The three phase voltages are not equal, which overheats motors and makes them vibrate.",
    source: "backend",
    how: "Biggest gap between one phase and the average of the three, as a % of that average. Warning above 3%, critical above 6%.",
  },
  typeTransients: {
    title: "Transients and Spikes",
    use: "Very high voltage surges from switching or lightning, which can destroy drives and power supplies.",
    source: "backend",
    how: "Average voltage above 345 V (150% of 230 V) for 3 minutes or less. Critical above 460 V (200%).",
  },
  typePhaseUnbalance: {
    title: "Phase Unbalance",
    use: "The three phase currents differ noticeably, which overheats motors and wastes energy.",
    source: "backend",
    how: "Biggest gap between one phase current and the average, as a % of that average, lasting at least 2 minutes in a row. Warning above 3%, critical above 6%.",
  },
  typePhaseLoss: {
    title: "Phase Loss",
    use: "One phase of a three-phase machine stopped carrying current while the other two still do.",
    source: "backend",
    how: "Three-phase machines only: at least two phases above 0.5 A and one at 0.1 A or less (or missing). Warning after 5 seconds, critical after 30 seconds. With minute-by-minute readings, any spell of 2 minutes or more is Critical.",
  },
  typeLeakageCurrent: {
    title: "Leakage Current",
    use: "Current is still flowing while the machine should be off, which points to damaged insulation or damp.",
    source: "backend",
    how: "While the machine is off (average current under 10% of rated), any phase above 5% of rated current. Warning after 10 minutes, critical after 30. Rated current = Rated Power ÷ (230 V × 0.85).",
  },
  typeInrushCurrent: {
    title: "Inrush Current",
    use: "A surge of current when a motor starts or a transformer is switched on.",
    source: "backend",
    how: "Highest phase current above 2× rated current for 3 minutes or less. Critical above 4×. Rated current = Rated Power ÷ (230 V × 0.85).",
  },
  typeHighPower: {
    title: "High Power",
    use: "The machine pulled more than its rating, which overheats windings and cables and can trip protection.",
    source: "backend",
    how: "Power above 120% of Rated Power for at least 2 minutes in a row. Critical above 140%. Needs Rated Power under Info > Node Settings.",
  },
  typeLowPower: {
    title: "Low Power",
    use: "A running machine pulled far less than its rating, which points to a fault or to running with nothing to do.",
    source: "backend",
    how: "While running (above 0.5 kW), power below 80% of Rated Power for at least 2 minutes in a row. Critical below 60%. A machine that is switched off is not flagged.",
  },
  typeMdiExceedance: {
    title: "MDI Above Sanctioned Load",
    use: "Your site's peak demand went above the sanctioned load, which the utility fines: about PKR 1,250 each time.",
    source: "backend",
    how: "Adds up all grid meters and takes a 30-minute rolling average. Each unbroken spell above your Sanctioned Load (Info > Details) counts as one breach. Warning above 5 breaches, critical above 10. The fine is shown in the detail line but not added to the loss.",
  },
  typeOnInOffTime: {
    title: "Machine On In Off Time",
    use: "The machine ran outside its working hours, so that energy was wasted.",
    source: "backend",
    how: "Above 0.5 kW outside the Working Hours set under Info > Node Settings, for at least 5 minutes in a row. Critical from 30 minutes. The detail line prices the wasted kWh at your grid unit price, or PKR 50/kWh if you have not set one.",
  },
  typeOffInOnTime: {
    title: "Machine Off In On Time",
    use: "The machine stood idle during its working hours, so output was lost.",
    source: "backend",
    how: "At or below 0.5 kW inside the Working Hours set under Info > Node Settings, for at least 5 minutes in a row. Critical from 30 minutes. Gaps with no readings do not count as idle.",
  },
  typeLowPf: {
    title: "Low Power Factor",
    use: "More current is drawn for the same work, which raises utility penalties and heats cables.",
    source: "backend",
    how: "Lagging power factor below 0.85 while the machine draws more than 0.5 kW. Critical below 0.70.",
  },
  typePfFluctuation: {
    title: "Power Factor Fluctuation",
    use: "Power factor jumps about as the load changes, which wears out capacitor banks and correction panels.",
    source: "backend",
    how: "Power factor moves by more than 0.005 from one minute to the next, in bursts of 3 minutes or less. Critical above 0.010.",
  },
  typeLeadingPf: {
    title: "Leading Power Factor",
    use: "The capacitors are over-correcting, which pushes voltage up and can bring penalties.",
    source: "backend",
    how: "Leading power factor below 0.85 while the machine draws more than 0.5 kW. Critical below 0.70.",
  },
  typeHighDailyEnergy: {
    title: "High Daily Energy Consumption",
    use: "A day's use ran above the recent average, a sign of overloading or a fault building up.",
    source: "backend",
    how: "The day's kWh against the average of the 3 days before it. Warning above 10% higher, critical above 30%. Machine and grid meters only.",
  },
  typeHighMonthlyEnergy: {
    title: "High Monthly Energy Consumption",
    use: "A month's use ran above the recent average, usually a lasting fault or a change in the way you work.",
    source: "backend",
    how: "The month's kWh against the average of the 3 months before it. Warning above 10% higher, critical above 30%. Machine and grid meters only.",
  },
  typeHighDailyGenset: {
    title: "High Daily Energy Produced by Genset",
    use: "The generator made more than usual in a day, so you burned fuel instead of using a cheaper source.",
    source: "backend",
    how: "The generator's kWh for the day against its own average over the 3 days before. Warning above 10% higher, critical above 30%.",
  },
  typeHighMonthlyGenset: {
    title: "High Monthly Energy Produced by Genset",
    use: "The site leaned on the generator much more this month than in recent months.",
    source: "backend",
    how: "The generator's kWh for the month against its own average over the 3 months before. Warning above 10% higher, critical above 30%.",
  },
  typeLowDailySolar: {
    title: "Low Daily Energy Produced by Solar PV",
    use: "Solar made less in a day than the weather says it should have.",
    source: "backend",
    how: "Actual kWh against what the weather says to expect, over hours that have both (at least 6 in the day). Warning above 10% short, critical above 30%. Needs the solar node's panel capacity and city.",
  },
  typeLowMonthlySolar: {
    title: "Low Monthly Energy Produced by Solar PV",
    use: "Solar fell short of what the weather says to expect for a whole month, usually dirty panels or a hardware fault.",
    source: "backend",
    how: "Actual kWh against what the weather says to expect, over hours that have both (at least 100 in the month). Warning above 10% short, critical above 30%.",
  },
  typeUnderFrequency: {
    title: "Under Frequency",
    use: "Supply frequency dropped too low, which risks sensitive equipment and generator sync.",
    source: "backend",
    how: "Frequency below 48.5 Hz. One low minute is a Warning; 2 minutes or more in a row is Critical.",
  },
  typeOverFrequency: {
    title: "Over Frequency",
    use: "Supply frequency rose too high, usually too much generation or a sudden loss of load.",
    source: "backend",
    how: "Frequency above 51.5 Hz. One high minute is a Warning; 2 minutes or more in a row is Critical.",
  },
  typeFrequencyFluctuations: {
    title: "Frequency Fluctuations",
    use: "Frequency keeps swinging, usually a generator governor that is not holding steady.",
    source: "backend",
    how: "How widely the last 5 minute-by-minute readings spread apart (their variance) above 2. Critical above 4.",
  },
  typeHighThdv: {
    title: "High THD Voltage",
    use: "Voltage distortion from harmonics that stays high, which overheats equipment and causes nuisance tripping.",
    source: "backend",
    how: "Voltage distortion above 5% for at least 60 minutes in a row. Critical above 8% for at least 60 minutes.",
  },
  typeIntermittentThdv: {
    title: "Intermittent THD Voltage",
    use: "Voltage distortion from harmonics that climbs for short spells during certain work.",
    source: "backend",
    how: "Voltage distortion above 5% for at least 1 minute. Critical above 8%.",
  },
  typeHighThdi: {
    title: "High THD Current",
    use: "Current distortion from harmonics that stays high, which overheats transformers and neutral wires.",
    source: "backend",
    how: "Current distortion above 10% for at least 60 minutes in a row. Critical above 20% for at least 60 minutes.",
  },
  typeIntermittentThdi: {
    title: "Intermittent THD Current",
    use: "Current distortion from harmonics that climbs for short spells during certain work.",
    source: "backend",
    how: "Current distortion above 10% for 1 to 59 minutes in a row. Critical above 20%.",
  },
};

export default help;
